/**
 * The idle world's state (D240 to D244). Plain JSON, safe to store as is.
 *
 * Two parts: `year` is everything the harvest festival resets (producers, upgrades, goals,
 * coins, chains); `permanent` is everything it keeps (Heritage, festival perks, the barn, lifetime
 * figures). Timed bonuses sit beside them with the times they start and end.
 */
import type { IdleContent, IdleDecorationKind } from '@hedgerow/data';
import { IDLE_GOAL_RULES } from './goals.ts';
import { idleDefaultLayout, idleLayoutFor } from './layout.ts';

export interface IdleProducerState {
  owned: number;
  /** A hand runs the bar without tapping. */
  hand: boolean;
  /** The bar is filling or full. A manual bar stops when full until it is tapped. */
  running: boolean;
  /** Time into the current bar, measured against `cycleMs`. A manual bar holds one full bar. */
  progressMs: number;
  /** The bar length `progressMs` refers to. When the bar's length changes, progress is rescaled. */
  cycleMs: number;
  /**
   * D277: time taps have taken off the current bar of a hand-run producer, in the same units as
   * `progressMs`. At most `content.tap.maxShare` of the bar; back to 0 when the bar pays.
   */
  tapped?: number;
}

/** What the harvest festival resets. */
export interface IdleYearState {
  /** 1 for the first year, rising with each festival. */
  number: number;
  startedAt: number;
  coins: number;
  /** Unlocked producers only, by id. A locked producer has no entry. */
  producers: Record<string, IdleProducerState>;
  /** Almanac upgrades bought this year, by id. */
  upgrades: string[];
  /** D457: mastery levels bought this year, by Almanac branch. Absent: none. */
  mastery?: Partial<Record<string, number>>;
  /** Chain buildings built this year (the mill, the dairy), by id. */
  chains: string[];
  /** Goals met this year, by id. */
  goals: string[];
  /** Coins earned this year. */
  earned: number;
  /** Heritage this year's goals have earned, paid by the festival on top of its own (D252). */
  heritage?: number;
  /**
   * D372: this year's goal targets round to friendly numbers. Absent on a year begun before, which
   * keeps the targets it had until the next festival.
   */
  friendlyGoals?: true;
  /**
   * D386: what unlock and hand prices are multiplied by this year: the Heritage income multiplier
   * the year started with, to `prices.heritagePower`. Fixed all year. Absent: 1, as on a year
   * begun before, which keeps its prices until its next festival.
   */
  priceFactor?: number;
}

/** One harvest festival held. */
export interface IdleFestivalRecord {
  /** The year it ended. */
  year: number;
  /** Heritage it paid, goals' Heritage included. */
  heritage: number;
  /** The part of `heritage` the year's goals earned (D252). */
  fromGoals?: number;
  /** Content id of the mark it left on the map. */
  mark: string;
  at: number;
}

/** What the harvest festival keeps. */
export interface IdlePermanentState {
  /** Heritage to spend on festival perks (D246). */
  heritage: number;
  /** Heritage earned in every festival. Sets the income bonus and only ever rises (D246). */
  heritageEarned: number;
  /**
   * D299: Heritage this generation's earnings stand for beyond what its festivals paid, set on
   * loading a save made under the square-root rule so its next festival pays on from where the
   * last one stopped. 0 for a world made since; back to 0 when the world is passed on.
   */
  heritageOffset?: number;
  /**
   * D325: Lineage this generation's play has added to the meter, whole and part, paid when the
   * world is passed on. Absent: none.
   */
  lineageMeter?: number;
  /** Festival shop perks owned, by id, with their level. */
  perks: Record<string, number>;
  /** Every festival held, oldest first. Each leaves a lasting mark on the map (D245). */
  festivals: IdleFestivalRecord[];
  /** 1-based. The barn is a building, not a crop, and survives the festival. */
  barnLevel: number;
  /** Every coin ever earned, across every year. */
  lifetimeEarned: number;
  /** Passing the world on (D257). Absent until the world is first passed on. */
  lineage?: IdleLineageState;
  /** D290: everything the player has found, for the collection book. Absent until the first. */
  collection?: IdleCollectionState;
  /** D291: decorations bought and chosen. Absent until the first is bought or chosen. */
  decorations?: IdleDecorationsState;
  /**
   * D331: Trimmings to spend on decorations, kept for good. Absent on a save made before D331,
   * which `loadIdleState` brings up to 0 with the Heritage it spent on decorations refunded.
   */
  trimmings?: number;
  /**
   * D326, D330: rosettes earned, oldest first, by id from `content.collection.rosettes`, taken in
   * turn and repeating past the end. Its length (capped in the data) sets the income multiplier.
   */
  rosettes?: string[];
  /**
   * D326: the rolling window towards the next rosette, oldest first: whether each of the last up
   * to `content.rosettes.window` weeks won the weekly event. Cleared once a rosette is earned, so
   * the next one starts its own window; a missed week never removes a rosette already earned.
   */
  rosetteWindow?: boolean[];
  /**
   * D334: long goals met, by key (`id` for the first tier, `id@2` on), in the generation named.
   * Kept through festivals; a record from an earlier generation counts as none, so passing the
   * world on resets them. Absent until the first is met.
   */
  longGoals?: { generation: number; met: string[] };
  /**
   * D335: the goal rules (`IDLE_GOAL_RULES`) this world's met goals were last settled under. A
   * save with an older or no stamp has every goal tier it already reached met quietly on load,
   * so a rules change is never shown as news. Absent on a save made before D335.
   */
  goalRules?: number;
  /**
   * D454: the Almanac fork sides taken in the generation named, by fork id, so the other side
   * stays closed through its festivals. A record from an earlier generation counts as none, so
   * passing the world on opens every fork again. Set on every world made since D454;
   * `loadIdleState` sets it once on an older save from the year's upgrades.
   */
  forks?: { generation: number; chosen: Record<string, string> };
  /**
   * D454: the world was made before the Almanac became a tree, so the screen shows its one-time
   * note ("Your Almanac is a tree now"). Set once by `loadIdleState`; the screen remembers the
   * note was closed. Absent on a world made since.
   */
  treeNote?: true;
}

/**
 * D290: the collection book's record, kept for good across festivals and generations. Each list
 * holds content ids, in content order.
 */
export interface IdleCollectionState {
  breeds: string[];
  tiers: string[];
  marks: string[];
  seasons: string[];
  visitors: string[];
  /** D304: weekly event prizes won. Absent on records kept before D304. */
  prizes?: string[];
  /** D327: monument-ground styles found. Absent on records kept before D330. */
  monuments?: string[];
  /** D331: seasonal decorations bought. Absent on records kept before D330. */
  seasonal?: string[];
  /** D326: rosettes earned. Absent until the rosette work lands; see `rosettes` above. */
  rosettes?: string[];
  /** D331: decoration sets completed. Absent until another lane names one. */
  sets?: string[];
}

/** D291: decorations, kept for good. A kind with nothing chosen shows its default (its first). */
export interface IdleDecorationsState {
  /** Bought, by id. Defaults are free and never listed. */
  owned: string[];
  /** The decoration shown for each kind, by kind. */
  chosen: Partial<Record<IdleDecorationKind, string>>;
}

/** What passing the world on keeps for good (D257). */
export interface IdleLineageState {
  /** Lineage to spend on traits. */
  points: number;
  /** Lineage earned in every passing on. */
  earned: number;
  /** Traits owned, by id, with their level. */
  traits: Record<string, number>;
  /** 1 for the first generation, rising each time the world is passed on. */
  generation: number;
  /** `festivals.length` when this generation began, so its own festivals can be counted. */
  festivalsBefore: number;
  /** `lifetimeEarned` when this generation began, so its own earnings can be counted. */
  lifetimeBefore?: number;
  /** Heritage earned by the generation before this one, from which a trait keeps a share (D263). */
  lastHeritage?: number;
  /** Heritage this generation must pass before its festivals pay (D264). */
  heritageFloor?: number;
  /** Heritage kept from the last generation by traits (D263, D264). */
  kept?: number;
}

/** A bonus that lifts a stat between two times (a market day, good weather). */
export interface IdleTimedBonus {
  /** Content id of the bonus, for the screen. */
  source: string;
  stat: IdleStat;
  /** One producer, or every producer when absent. */
  producer?: string;
  factor: number;
  from: number;
  until: number;
}

/** speed shortens bars, value raises payouts, tap raises tapped payouts, find raises lucky finds. barn no longer changes the barn (D253). */
export type IdleStat = 'speed' | 'value' | 'tap' | 'barn' | 'hand' | 'upgrade' | 'find';

export interface IdleWorldState {
  version: 1;
  createdAt: number;
  /** The last time the player was here (any action or check-in). The barn counts from it. */
  lastSeen: number;
  /** The time the state has been advanced to. */
  advancedTo: number;
  /** `permanent.lifetimeEarned` at `lastSeen`, so the welcome-back sum is right however often the state is advanced. */
  earnedAtSeen: number;
  year: IdleYearState;
  permanent: IdlePermanentState;
  bonuses: IdleTimedBonus[];
  /**
   * The region goal's reward (D244), set by the server and never by an action: a value
   * multiplier for every producer between `from` and `until`. Absent when none is due.
   */
  region?: IdleRegionBonus;
  /** Seeds the chance bonuses (D244). Absent on older worlds, which use `createdAt`. */
  seed?: number;
  /** Market days and lucky finds already claimed, by offer id, so each is claimed once. */
  claimed?: string[];
  /**
   * D276: the festival has been held and the new year not yet started. Heritage is paid and
   * perks can be bought; nothing earns, and `startIdleYear` starts the year. Absent otherwise.
   */
  festivalPending?: IdleFestivalPending;
  /** D267: where things sit on the land. Never read by anything that earns. */
  layout?: IdleLayout;
  /** D304: the day's orders, the run of daily visits and the week's event. Absent until a check-in. */
  daily?: IdleDailyState;
}

/** D304: one of the day's orders: send `count` of what `producer` makes. */
export interface IdleOrderState {
  producer: string;
  count: number;
  /** Paid: the order was filled. */
  done?: boolean;
}

/**
 * D304: what the day and the week have come to. Set on the first check-in of each day, by the
 * day number from the server's clock.
 */
export interface IdleDailyState {
  /** The day number of the last check-in: whole days since 1 January 1970, UTC. */
  day: number;
  /** The day's orders, one for each size in `content.daily.orders.sizes`. */
  orders: IdleOrderState[];
  /** What each producer has made since the orders came, by id: units of a bar times owned. */
  made: Record<string, number>;
  /** Days in a row with a visit, today counted. */
  streak: number;
  /** The week number `weekOrders`, `weekDays` and `eventWon` count in. */
  week: number;
  /** Orders filled this week. */
  weekOrders: number;
  /** Days visited this week, today counted. */
  weekDays: number;
  /** The week's event goal is met and its prize given. */
  eventWon?: boolean;
}

/** A festival held whose new year has not started (D276). */
export interface IdleFestivalPending {
  /** The year that ended. The year to start is this plus one. */
  year: number;
  /** Heritage it paid, goals' Heritage included. */
  heritage: number;
  /** When it was held. */
  at: number;
  /**
   * D388: the world was passed on, not a festival held: `heritage` is the Heritage the new
   * generation keeps, and Lineage traits can be chosen before `startIdleYear` begins it.
   */
  passedOn?: true;
}

/** A rectangle of map tiles: `x`, `y` its back corner, `w` by `h` tiles. */
export interface IdleLayoutRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** A map tile. */
export interface IdleLayoutTile {
  x: number;
  y: number;
}

/**
 * Where things sit on the world's land (D267). Made once from the world's seed and stored, so a
 * world's map never moves. Tiles are on the map's grid; the world proper is `width` by `height`
 * from 0,0, and trees and the stream stand outside it, up to `margin` tiles out.
 */
export interface IdleLayout {
  /** The seed it was made from; 0 for the default layout older worlds are given. */
  seed: number;
  width: number;
  height: number;
  margin: number;
  /** True when the default plan is flipped east to west: buildings drawn on a plot flip with it. */
  mirrored: boolean;
  /** The yard the farmhouse and barn stand on. */
  yard: IdleLayoutRect;
  /** Each producer's land on the world, by id. Producers on a piece of land beside it are in `lands`. */
  plots: Record<string, IdleLayoutRect>;
  /**
   * The festival marks: the `i`th is 2 by 2 at x + (i mod perRow) x dx, y + floor(i / perRow) x dy.
   * `idleMarkTile` works it out.
   */
  marks: { x: number; y: number; dx: number; dy: number; perRow: number };
  /** Single trees, outside the world. */
  trees: IdleLayoutTile[];
  /** The stream's tiles in order, from one edge of the map to the other; empty when there is none. */
  stream: IdleLayoutTile[];
  /**
   * D287: the river's tiles in order, from one edge of the map to the other, outside the world and
   * never behind it. A world with a stream has its stream as its river. Absent on layouts stored
   * before D287; `idleLayout` always fills it in, the same for the same seed.
   */
  river?: IdleLayoutTile[];
  /**
   * D301: each piece of land beside the world, by `content.map.lands` id: a rectangle outside the
   * world on a side the river does not run along, with its producers' plots. Drawn only once a
   * trait opens it (`idleOpenLands`). `idleLayout` always fills it in, the same for a seed.
   */
  lands?: Record<string, IdleLayoutLand>;
  /**
   * D344: the kind of world its seed picked, by `content.map.kinds` id, and the traits its land
   * has, by `content.map.traits` id. Absent on a world made before kinds.
   */
  kind?: string;
  traits?: string[];
  /** D344: ground within `width` by `height` that is outside the fence, so the outline is not
   * always a rectangle. Trees may stand there. Absent: the whole rectangle is the world. */
  cuts?: IdleLayoutRect[];
  /** D344: a pond among the fields, on the world. */
  pond?: IdleLayoutRect;
  /**
   * D440: lone tiles on the world, as "x,y" keys, that no plot, yard, mark or pond covers and
   * whose four neighbours are all covered or off the world. Each takes a filler. Absent: none.
   */
  fillers?: string[];
}

/** D301: a piece of land beside the world, in map tiles, with its own producers' plots by id. */
export interface IdleLayoutLand extends IdleLayoutRect {
  plots: Record<string, IdleLayoutRect>;
}

/** The week's multiplier a region pays every member world after meeting its goal. */
export interface IdleRegionBonus {
  factor: number;
  from: number;
  until: number;
}

/** A fresh year: the starting coins and every free producer, one owned, bar idle. */
export function newIdleYear(content: IdleContent, number: number, now: number): IdleYearState {
  const producers: Record<string, IdleProducerState> = {};
  for (const p of content.producers) {
    if (p.unlockCost === 0)
      producers[p.id] = {
        owned: 1,
        hand: false,
        running: false,
        progressMs: 0,
        cycleMs: p.cycleMs,
      };
  }
  return {
    number,
    startedAt: now,
    friendlyGoals: true,
    coins: content.startCoins,
    producers,
    upgrades: [],
    chains: [],
    goals: [],
    earned: 0,
  };
}

/**
 * A new world at time `now`. `seed` (D267) shapes the land: where the yard, plots, trees and stream
 * sit. It never changes what the world earns, and without it the world gets the default layout.
 */
export function createIdleWorld(content: IdleContent, now: number, seed?: number): IdleWorldState {
  return {
    version: 1,
    createdAt: now,
    lastSeen: now,
    advancedTo: now,
    earnedAtSeen: 0,
    year: newIdleYear(content, 1, now),
    permanent: {
      heritage: 0,
      heritageEarned: 0,
      heritageOffset: 0,
      perks: {},
      festivals: [],
      barnLevel: 1,
      lifetimeEarned: 0,
      trimmings: 0,
      goalRules: IDLE_GOAL_RULES,
      forks: { generation: 1, chosen: {} },
    },
    bonuses: [],
    seed: now >>> 0,
    layout: seed === undefined ? idleDefaultLayout(content) : idleLayoutFor(content, seed),
  };
}
